import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, ImagePlus, Loader2, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FIELD } from "@/components/forms/Field";
import { submitService, errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

const EMPTY = { name: "", business: "", machine: "", problem: "", phone: "" };

const PhotoDrop = ({ file, setFile }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (f.size > 10 * 1024 * 1024) return toast.error("Photo must be under 10MB.");
    setFile(f);
  };
  if (preview)
    return (
      <div className="relative mt-3 flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-950/40 p-3" data-testid="service-photo-preview">
        <img src={preview} alt="Machine" className="h-16 w-16 rounded-xl object-cover" />
        <span className="flex-1 truncate text-sm text-slate-300">{file.name}</span>
        <button type="button" onClick={() => setFile(null)} className="grid h-9 w-9 place-items-center rounded-full border border-white/15 hover:border-signal" data-testid="service-photo-remove" aria-label="Remove photo"><X className="h-4 w-4" /></button>
      </div>
    );
  return (
    <button
      type="button"
      onClick={() => input.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
      className={cn("mt-3 flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed px-6 py-8 text-sm transition-[border-color,background-color] duration-300", drag ? "border-volt bg-volt/5" : "border-white/15 hover:border-white/40")}
      data-testid="service-photo-dropzone"
    >
      <ImagePlus className="h-6 w-6 text-volt" />
      <span className="text-slate-300">Drop a photo or <span className="text-volt underline underline-offset-4">browse</span></span>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">JPG / PNG · up to 10MB</span>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="service-photo-input" />
    </button>
  );
};

export const ServiceForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("photo", file);
    try {
      await submitService(fd);
      toast.success("Service request sent.");
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-10" data-testid="service-form-success">
        <div className="grid h-14 w-14 place-items-center rounded-full bg-volt text-ink-950"><Check className="h-6 w-6" /></div>
        <h3 className="mt-6 font-display text-3xl font-bold text-white">Service request received.</h3>
        <p className="mt-3 max-w-md text-slate-400">Our service team will contact you shortly. For urgent issues, call (931) 473-3465.</p>
        <button onClick={() => { setForm(EMPTY); setFile(null); setDone(false); }} className="btn-ghost mt-8 h-11 px-5 text-sm" data-testid="service-form-reset">Submit another</button>
      </motion.div>
    );

  return (
    <form onSubmit={submit} className="grid gap-x-8 gap-y-7 sm:grid-cols-2" data-testid="service-form">
      <Field label="Name" htmlFor="svc-name" required>
        <Input id="svc-name" required value={form.name} onChange={set("name")} placeholder="Full name" className={FIELD} data-testid="service-name-input" />
      </Field>
      <Field label="Business" htmlFor="svc-business" required>
        <Input id="svc-business" required value={form.business} onChange={set("business")} placeholder="Business name" className={FIELD} data-testid="service-business-input" />
      </Field>
      <Field label="Machine" htmlFor="svc-machine" required>
        <Input id="svc-machine" required value={form.machine} onChange={set("machine")} placeholder="e.g. Claw crane, jukebox" className={FIELD} data-testid="service-machine-input" />
      </Field>
      <Field label="Phone" htmlFor="svc-phone" required>
        <Input id="svc-phone" type="tel" required minLength={7} value={form.phone} onChange={set("phone")} placeholder="(931) 000-0000" className={FIELD} data-testid="service-phone-input" />
      </Field>
      <Field label="Problem" htmlFor="svc-problem" required className="sm:col-span-2">
        <Textarea id="svc-problem" required value={form.problem} onChange={set("problem")} rows={3} placeholder="What's happening with the machine?" className={cn(FIELD, "min-h-[96px] resize-none py-3")} data-testid="service-problem-input" />
      </Field>
      <Field label="Photo (optional)" className="sm:col-span-2">
        <PhotoDrop file={file} setFile={setFile} />
      </Field>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="btn-primary h-14 w-full px-8 text-base disabled:opacity-60 sm:w-auto" data-testid="service-submit-button">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Request Service {!loading && <ArrowRight className="btn-arrow h-4 w-4" />}
        </button>
      </div>
    </form>
  );
};
